import React, { createContext, useContext, useState, useEffect } from 'react';
import heTranslations from '../config/translations/he.json';
import enTranslations from '../config/translations/en.json';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  // Get initial language from localStorage or default to Hebrew
  const getInitialLanguage = () => {
    const savedLanguage = localStorage.getItem('language');
    return savedLanguage || 'he';
  };

  const [language, setLanguage] = useState(getInitialLanguage);
  const [translations, setTranslations] = useState(
    language === 'he' ? heTranslations : enTranslations
  );

  // Update translations when language changes
  useEffect(() => {
    const newTranslations = language === 'he' ? heTranslations : enTranslations;
    setTranslations(newTranslations);
    
    // Always keep document direction as LTR - this is required for Slick Carousel to work
    // Hebrew text will naturally flow RTL within the LTR document structure
    document.documentElement.dir = 'ltr';
    
    // Update document language attribute for accessibility and SEO
    document.documentElement.lang = language;
    
    // Save to localStorage
    localStorage.setItem('language', language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prevLang => prevLang === 'he' ? 'en' : 'he');
  };

  const setLanguageTo = (lang) => {
    if (lang === 'he' || lang === 'en') {
      setLanguage(lang);
    }
  };

  // Helper function to get nested translation by path (e.g., 'navigation.home')
  const t = (path) => {
    const keys = path.split('.');
    let value = translations;
    
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        console.warn(`Translation not found for path: ${path}`);
        return path; // Return the path itself if translation not found
      }
    }
    
    return value;
  };

  const isRTL = language === 'he';
  const isHebrew = language === 'he';
  const isEnglish = language === 'en';

  const value = {
    language,
    toggleLanguage,
    setLanguage: setLanguageTo,
    translations,
    t,
    isRTL,
    isHebrew,
    isEnglish
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;

